import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import axios from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import useDocumentTitle from '../hooks/useDocumentTitle';
import SEOHead from '../components/SEOHead';

const FEATURES = [
  { f: 'Daily Quiz Access', free: true, prem: true },
  { f: 'See What You Got Right', free: true, prem: true },
  { f: 'Public Leaderboards', free: true, prem: true },
  { f: 'See What You Got Wrong', free: false, prem: true },
  { f: 'See your Quiz History', free: false, prem: true },
  { f: 'Premium Badge', free: false, prem: true },
];

const Premium: React.FC = () => {
  useDocumentTitle('Cognidaily | Premium');
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [isGrantingPremium, setIsGrantingPremium] = useState(false);
  const [isCancellingPremium, setIsCancellingPremium] = useState(false);
  const isPremium = user?.isPremium ?? false;
  const { refreshProfile } = useAuth();

  useEffect(() => {
    if (showComingSoon || showManageModal) document.body.classList.add('premium-modal-open');
    else document.body.classList.remove('premium-modal-open');
    return () => document.body.classList.remove('premium-modal-open');
  }, [showComingSoon, showManageModal]);

  const handleUpgradeClick = async () => {
    if (!isAuthenticated) navigate('/login');
    else if (!isPremium) {
      try { await axios.post('/api/user/premium/upgrade-click'); } catch {}
      setShowComingSoon(true);
    }
  };

  const handleGrantPremium = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setIsGrantingPremium(true);
    try {
      await axios.post('/api/user/premium/grant-free');
      window.location.reload();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed.');
      setIsGrantingPremium(false);
    }
  };

  const handleCancelPremium = async () => {
    if (!isAuthenticated) return;
    setIsCancellingPremium(true);
    try {
      await axios.post('/api/user/premium/cancel');
      await refreshProfile();
      setShowManageModal(false);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to cancel.');
    } finally {
      setIsCancellingPremium(false);
    }
  };

  const formatExpiryDate = (iso?: string | null) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return null;
    }
  };

  const renderCheck = (value: boolean) => (
    value ? <span className="premium-feature__check" aria-hidden>✓</span> : <span className="premium-feature__dash" aria-hidden>−</span>
  );

  return (
    <>
      <SEOHead title="Premium" description="Unlock premium features." url="https://cognidaily.io/premium" />
      <article className="premium-page">
        <header className="premium-page__header">
          <h1 className="premium-page__title">
            {isPremium ? 'Your Premium Benefits' : 'Unlock Premium'}
          </h1>
        </header>

        <section className="premium-page__hero card">
          <p className="premium-page__tagline">
            {isPremium ? 'Thanks for being a premium member!' : 'Become a premium member today for $1AU/month, cancel anytime.'}
          </p>
          {isPremium ? (
            <p className="premium-page__cta">
              <button type="button" className="link link--button" onClick={() => setShowManageModal(true)}>
                Manage Premium
              </button>
            </p>
          ) : (
            <div className="premium-page__actions">
              {!isAuthenticated ? (
                <Link to="/login" className="btn btn-primary btn--mobile-full">Sign in to Upgrade</Link>
              ) : (
                <button type="button" className="btn btn-primary btn--mobile-full" onClick={handleUpgradeClick}>
                  Upgrade to Premium
                </button>
              )}
            </div>
          )}
        </section>

        <section className="premium-page__explainer card">
          <h2 className="premium-page__section-title">Quiz history</h2>
          <p className="premium-page__explainer-text">
            As a paying member of Cognidaily, we&rsquo;ll store your quiz history for you. View all your past quizzes in your profile, see which questions you got right <em>and</em> wrong, and track your progress over time.
          </p>
          <p className="premium-page__explainer-text premium-page__explainer-text--muted">
            Free users can see a brief breakdown (correct answers only) right after each quiz, but quiz history is not saved to their profile.
          </p>
        </section>

        <section className="premium-page__comparison card">
          <h2 className="premium-page__section-title">Feature Comparison</h2>
          <div className="table-wrap">
            <table className="premium-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Free</th>
                  <th>Premium</th>
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((item, i) => (
                  <tr key={i}>
                    <td>{item.f}</td>
                    <td>{renderCheck(item.free)}</td>
                    <td>{renderCheck(item.prem)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

      </article>

      {showComingSoon &&
        createPortal(
          <div
            className="result-modal-overlay"
            onClick={() => setShowComingSoon(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="premium-modal-title"
          >
            <div className="result-modal premium-modal" onClick={(e) => e.stopPropagation()}>
              <h2 id="premium-modal-title" className="result-modal__title">Premium for Early Adopters</h2>
              <div className="result-modal__body">
                <p>Premium is free for early adopters. Click &ldquo;Try Premium&rdquo; below and you&rsquo;ll be assigned premium membership.</p>
              </div>
              <div className="result-modal__actions premium-modal__actions">
                <button
                  type="button"
                  className="btn btn-primary result-modal__button"
                  onClick={handleGrantPremium}
                  disabled={isGrantingPremium}
                >
                  {isGrantingPremium ? '...' : 'Try Premium'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowComingSoon(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {showManageModal &&
        createPortal(
          <div
            className="result-modal-overlay"
            onClick={() => setShowManageModal(false)}
            role="dialog"
            aria-modal="true"
            aria-labelledby="manage-premium-modal-title"
          >
            <div className="result-modal premium-modal" onClick={(e) => e.stopPropagation()}>
              <h2 id="manage-premium-modal-title" className="result-modal__title">Manage Premium</h2>
              <div className="result-modal__body">
                <div className="manage-premium__status">
                  <span className="manage-premium__check" aria-hidden>✓</span>
                  <span>You&rsquo;re a premium member</span>
                </div>

                <dl className="manage-premium__details">
                  {user?.premiumStartedAt && (
                    <div className="manage-premium__row">
                      <dt>Member since</dt>
                      <dd>{formatExpiryDate(user.premiumStartedAt) ?? '—'}</dd>
                    </div>
                  )}
                  <div className="manage-premium__row">
                    <dt>{user?.premiumCancelledAt ? 'Access until' : 'Active until'}</dt>
                    <dd>{formatExpiryDate(user?.premiumExpiresAt) ?? '—'}</dd>
                  </div>
                </dl>

                {user?.premiumCancelledAt ? (
                  <p className="manage-premium__note">
                    Your subscription is cancelled. You&rsquo;ll keep premium access until the date above.
                  </p>
                ) : (
                  <p className="manage-premium__note">
                    If you cancel, you&rsquo;ll keep premium access until then. Your subscription won&rsquo;t renew after that date.
                  </p>
                )}

                <div className="manage-premium__payment">
                  <button type="button" className="manage-premium__payment-btn" disabled title="Coming soon">
                    Update payment method
                  </button>
                  <span className="manage-premium__payment-hint">Coming soon</span>
                </div>
              </div>
              <div className="result-modal__actions premium-modal__actions">
                {!user?.premiumCancelledAt && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancelPremium}
                    disabled={isCancellingPremium}
                  >
                    {isCancellingPremium ? '...' : 'Cancel Premium'}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowManageModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default Premium;
